'use client';

import { initialize } from '@/utils/FirebaseUtil';
import { captureError } from '@/utils/sentry';
import { getAnalytics } from 'firebase/analytics';
import { FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  MessagePayload,
  Messaging,
  NextFn,
  Observer,
  onMessage,
} from 'firebase/messaging';
import { delayAsync } from 'libs/rtzr-commons/async/Delay';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';

type OnMessageHandlerType = NextFn<MessagePayload> | Observer<MessagePayload>;

type Store = {
  messaging: Messaging | undefined;
  pushToken: string | undefined;
  firebaseApp: FirebaseApp | undefined;
};

export const useFirebaseStore = create<Store>(() => ({
  messaging: undefined,
  pushToken: undefined,
  firebaseApp: undefined,
}));

export const usePushToken = (): string | undefined => {
  return useFirebaseStore((state) => state.pushToken);
};

const useInitFirebaseStore = () => {
  useEffect(() => {
    try {
      const firebaseApp = initialize();
      analyticsInstance.googleAnalytics = getAnalytics(firebaseApp);
      useFirebaseStore.setState({ firebaseApp });
    } catch (e) {
      captureError(new Error('[Firebase] 초기화 실패', { cause: e }));
    }
  }, []);
};

export const FirebaseStoreInitializer = () => {
  useInitFirebaseStore();
  return null;
};

export const useSetOnMessage = () => {
  const messaging = useFirebaseStore((state) => state.messaging);
  return useCallback(
    (handler: OnMessageHandlerType) => {
      if (messaging) {
        return onMessage(messaging, handler);
      }

      return undefined;
    },
    [messaging]
  );
};

export const PushTokenStoreInitializer = () => {
  useEffect(() => {
    async function generatePushToken() {
      try {
        const { ok } = isBrowserSupportedForFCM();
        if (!ok) {
          return;
        }

        const scriptURL = '/firebase-messaging-sw.js';
        const firebaseApp = useFirebaseStore.getState().firebaseApp;
        if (!firebaseApp) {
          return;
        }

        // Service Worker 지원 확인
        if (!('serviceWorker' in navigator)) {
          throw new Error('Service Worker not supported');
        }

        // 목록에 있는지 검사
        const serviceWorker = await navigator.serviceWorker.getRegistrations();
        const isRegistered = serviceWorker.some((sw) => sw.active?.scriptURL.includes(scriptURL));
        if (!isRegistered) {
          // Service Worker 등록
          try {
            const registration = await navigator.serviceWorker.register(scriptURL);
            await registration.update();
          } catch (e) {
            // 서비스 워커 등록 실패해도 pushToken 발급에 문제가 없음. 일단 넘어감.
            // captureError(new Error('[Firebase] Service Worker 등록 실패', { cause: e }));
          }
        }

        if (
          !('Notification' in window) ||
          !Notification.requestPermission ||
          !(await isSupported())
        ) {
          // 브라우저가 Notification API를 지원하지 않음.
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          // 유저가 권한 주지 않음.
          return;
        }

        const messaging = getMessaging(firebaseApp);
        useFirebaseStore.setState({ messaging });
        try {
          await retry(async () => {
            const token = await getToken(messaging, {
              vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
            });
            useFirebaseStore.setState({ pushToken: token });
          });
        } catch (e) {
          captureError(
            new Error('[Firebase] getMessaging 성공했으나 pushToken 발급 실패', { cause: e })
          );
        }
      } catch (e) {
        captureError(new Error('[Firebase] 초기화 실패', { cause: e }));
      }
    }

    generatePushToken();
  }, []);

  return null;
};

async function retry(fn: () => Promise<void>, retryCount = 3, interval = 1000) {
  for (let i = 0; i < retryCount; i++) {
    try {
      await fn();
      return;
    } catch (e) {
      if (i === retryCount - 1) {
        throw e;
      }
      await delayAsync(interval);
    }
  }
}

export function isBrowserSupportedForFCM(): { ok: boolean; reasons: string[] } {
  const reasons: string[] = [];

  if (typeof window === 'undefined') {
    reasons.push('server-side environment');
    return { ok: false, reasons };
  }

  if (!('serviceWorker' in navigator)) reasons.push('serviceWorker 미지원');
  if (!('PushManager' in window)) reasons.push('PushManager 미지원');
  if (!('Notification' in window)) reasons.push('Notification 미지원');

  // Notification permission API가 있다 하더라도, 브라우저 자체가 push 메시지 수신을 못할 수 있음
  try {
    if (!window.isSecureContext) reasons.push('보안 컨텍스트(https/localhost)가 아님');
  } catch (e) {
    // 일부 환경에서 접근 불가
  }

  return { ok: reasons.length === 0, reasons };
}
